import { useSelector } from "react-redux"
import { useEffect, useMemo, useCallback, useRef, memo } from "react"
import type { RootState } from "../../store"
import { useTheme } from "../../contexts/ThemeContext"
import { colors } from "../../styles/colors"
import { useTranslation } from "../../hooks/useTranslation"
import { navigateTo } from "../../utils/navigation"
import { fetchCart } from "../../features/cart/cartSlice"
import { useDispatch } from "react-redux"
import type { AppDispatch } from "../../store"

const BasketButton = memo(() => {
  const dispatch = useDispatch<AppDispatch>()
  const { showItemDetails, showLoginModal, showProductModal } = useSelector((state: RootState) => state.ui)
  const { items, isLoading, apiCartData } = useSelector((state: RootState) => state.cart)
  const { token } = useSelector((state: RootState) => state.auth)
  const { primaryColor } = useTheme()
  const { t, isRTL, currentLanguage } = useTranslation()


  // Memoize item count calculation to avoid recalculating on every render
  const itemCount = useMemo(() => {
    // Count unique items instead of total quantity
    const count = apiCartData?.items?.length || items?.length || 0
    return count
  }, [apiCartData?.items, items])

  // Show loading state for count
  const displayCount = isLoading ? "..." : itemCount

  // Memoize handle click to avoid recreating function on every render
  const handleClick = useCallback(() => {
    try {
      navigateTo("cart")
    } catch (error) {
    }
  }, [])

  // Don't show if modals are open
  if (showItemDetails || showLoginModal || showProductModal) {
    return null
  }

  return (
    <button
      className={`fixed bottom-5 left-1/2 -translate-x-1/2 text-white flex items-center gap-3 pt-2 pb-2 rounded-full z-[9999] shadow-lg hover:bg-gray-800 transition-colors ${isRTL ? 'pl-4 pr-2' : 'pl-2 pr-4'}`}
      onClick={handleClick}
      style={{ backgroundColor: "#1e1e1e" }}
    >
      {/* Salla Icon */}
      <div
        className="w-9 h-9  rounded-full flex items-center justify-center"
        style={{ backgroundColor: primaryColor }}
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M3.77952 5.625L3.82135 5.62501L16.2205 5.625C16.6307 5.62495 17.0043 5.62491 17.3035 5.66865C17.6323 5.71672 17.9827 5.83171 18.2437 6.15047C18.5019 6.46574 18.5501 6.83097 18.5406 7.16305C18.5317 7.46985 18.4687 7.84531 18.3987 8.26325L17.3823 14.3258C17.2217 15.2836 17.0919 16.0584 16.9132 16.6642C16.7276 17.2935 16.4693 17.8151 16.0045 18.2178C15.5378 18.6221 14.9887 18.7977 14.3472 18.8798C13.7334 18.9583 12.9663 18.9583 12.0237 18.9583H7.97624C7.03361 18.9583 6.26659 18.9583 5.65285 18.8798C5.01134 18.7977 4.46212 18.6221 3.99545 18.2178C3.53068 17.8151 3.27243 17.2935 3.08676 16.6642C2.90807 16.0584 2.7782 15.2837 2.61766 14.3259L1.60803 8.30319L1.60134 8.26328C1.53123 7.84533 1.46825 7.46985 1.45944 7.16305C1.4499 6.83097 1.49803 6.46574 1.75624 6.15046C2.0173 5.83171 2.36762 5.71672 2.6965 5.66865C2.99563 5.62491 3.36935 5.62495 3.77952 5.625Z" />
          <path fillRule="evenodd" clipRule="evenodd" d="M10 2.70835C8.15905 2.70835 6.66667 4.20074 6.66667 6.04169V6.45835C6.66667 6.91859 6.29357 7.29169 5.83333 7.29169C5.3731 7.29169 5 6.91859 5 6.45835V6.04169C5 3.28026 7.23857 1.04169 10 1.04169C12.7614 1.04169 15 3.28026 15 6.04169V6.45835C15 6.91859 14.6269 7.29169 14.1667 7.29169C13.7064 7.29169 13.3333 6.91859 13.3333 6.45835V6.04169C13.3333 4.20074 11.8409 2.70835 10 2.70835Z" />
        </svg>
      </div>

      {/* Text */}
      <span className="text-sm">{t('cart.basket', 'السلة')}</span>

      {/* Count */}
      <span
        className="rounded-full w-6 h-6 text-center text-sm font-bold flex items-center justify-center"
        style={{
          backgroundColor: itemCount === 0 ?"#343434" : primaryColor,
          color: itemCount === 0 ? colors.neutral.white :colors.neutral.white,
        }}
      >
        {displayCount}
      </span>
    </button>
  )
})

BasketButton.displayName = 'BasketButton'

export default BasketButton